/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    defineStore,
    acceptHMRUpdate } from 'pinia';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import sortBy from 'lodash/sortBy';
import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';
import { promiseAllObjectValues } from '@utils/promise.utils';
import { UTM_PARAMETERS } from '@/constants/utm-parameters';
import { i18n_t as $t } from '@/utils/i18n';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import {
    fetchVisitorMetadata,
    fetchAccountMetadata,
    fetchParentAccountMetadata,
    fetchEventMetadata,
    fetchObjectMetadata,
    removeSchema,
    updateSchema,
    updateRules,
    setSkipList,
    promoteSchema,
    updateViaEnabled,
    setObjectProperty,
    setObjectPropertyNameMetadata,
    generateMetadataDescription,
} from '@/stores/api/metadata.api';
import {
    isHistoricalMetadata,
    iterateThroughMetadata,
} from '@/stores/helpers/metadata.helper';
import { getEmailMetadataOptions } from '@pendo/services/EmailUtils';
import { objectMetadataRowPath } from '@pendo/middleware';

import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import { activeSubHasPurchasedProductLine } from '@/data/flags/product-lines.js';

type LoadAllBatch = { cardinality: boolean; promise: Promise<void> };

let loadAllPromise: LoadAllBatch | null = null;
let loadAllSeq = 0;
let appliedLoadAllSeq = 0;

export const useMetadataStore = defineStore('metadata', {
    state: () => ({
        map: {} as Record<string, any>,
        objectMap: {} as Record<string, Record<string, any>>,
        error: {} as any,
        fetching: false,
        updating: false,
        removing: false,
        includesCardinality: false,
    }),
    getters: {
        metadataListByKind: (state) => (kind: string, options: any = {}) => {
            const kindAdjusted = options.isUtm ? 'utm' : kind;
            const { groupsToExclude, allowedTypes } = options;
            const isGroupAllowed = (group: string) => {
                if (groupsToExclude && !isEmpty(groupsToExclude)) {
                    return !groupsToExclude.includes(group);
                }

                return true;
            };
            const isTypeAllowed = (type: string) => {
                if (!isEmpty(allowedTypes)) {
                    return allowedTypes.includes(type);
                }

                return true;
            };

            return iterateThroughMetadata(get(state.map, kind, []), kindAdjusted)
                .filter(({ group, Type }: any) => isGroupAllowed(group) && isTypeAllowed(Type));
        },
        metadataList (state): any[] {
            const includeParentAccounts = hasSubscriptionFlag('usesSubaccounts');

            const iterateArray: any[] = [
                ...iterateThroughMetadata(state.map.visitor, 'visitor'),
                ...iterateThroughMetadata(state.map.account, 'account'),
            ];
            iterateArray.push(...iterateThroughMetadata(state.map.event, 'event'));

            if (includeParentAccounts) {
                iterateArray.push(...iterateThroughMetadata(state.map.parentAccount, 'parentAccount'));
            }

            return iterateArray;
        },
        historicalMetadata (): any[] {
            if (activeSubHasPurchasedProductLine(PRODUCT_LINES.ADOPT)) {
                return [];
            }

            const kindArr = ['visitor', 'account', 'parentAccount'];
            const metadataArray: any[] = [];
            kindArr.forEach((kind) => metadataArray.push(...this.metadataListByKind(kind)));
            const historicalMetadata = metadataArray.filter(isHistoricalMetadata);

            return historicalMetadata;
        },
        visitorMetadataOptions (): any[] {
            const showAccounts = activeSubHasPurchasedProductLine(PRODUCT_LINES.CORE, 'accountsUIEnabled');
            const visitorMetadata = this.metadataListByKind('visitor', { groupsToExclude: ['auto', 'pendo'] });
            const availableVisitorMetadata = visitorMetadata.filter((singleField: any) => {
                const typesToDisplay = ['boolean', 'string', 'integer'];
                const hideAccountFields = !showAccounts && (singleField.field.includes('account'));

                return typesToDisplay.includes(singleField.Type) && !hideAccountFields;
            });
            const visitorMetaDataWithViewByDisplayName = availableVisitorMetadata.map(({ DisplayName, ...rest }: any) => ({
                ...rest,
                displayName: DisplayName,
            }));

            return sortBy(visitorMetaDataWithViewByDisplayName, [({ displayName }: any) => displayName.toLowerCase()]);
        },
        sessionProperties (): any[] {
            if (!activeSubHasPurchasedProductLine(PRODUCT_LINES.ANALYTICS, 'webAnalyticsEnabled')) {
                return [];
            }
            const utmParameters = UTM_PARAMETERS();
            const sessionPropertiesArray: any[] = (cloneDeep(
                this.metadataListByKind('event', { isUtm: true }),
            ) as any[]).filter(
                (item: any) => item.group === 'utm',
            );

            const channelSessionProperty: any[] = sessionPropertiesArray.filter((item: any) => {
                return item.field === 'channel';
            });
            if (channelSessionProperty.length > 0) {
                channelSessionProperty[0].DisplayName = $t('utmParameters.Channel');
            }

            let referrerSessionProperty: any[] = sessionPropertiesArray.filter((item: any) => {
                return item.field === 'referrer';
            });

            // We do not send a referrer if it is "direct", but user may want to filter on it
            if (isEmpty(referrerSessionProperty)) {
                referrerSessionProperty = [
                    {
                        field: 'referrer',
                        group: 'utm',
                        kind: 'utm',
                        Type: 'string',
                        ElementType: '',
                        uniqueValues: [],
                    },
                ];
            }
            if (referrerSessionProperty.length > 0) {
                referrerSessionProperty[0].DisplayName = $t('utmParameters.Referrer');
            }

            const automaticSessionProperties = [...channelSessionProperty, ...referrerSessionProperty];
            const utmParametersByKind = keyBy(utmParameters, 'field');
            const kindMap = new Map(sessionPropertiesArray.map((item: any) => [item.field, item]));
            const sortedUtmSessionProperties = Object.keys(utmParametersByKind)
                .filter((kind) => kindMap.has(kind))
                .map((kind) => {
                    return {
                        ...kindMap.get(kind),
                        DisplayName: (utmParametersByKind as any)[kind].DisplayName,
                    };
                });

            return [...automaticSessionProperties, ...sortedUtmSessionProperties];
        },
        accountMetadataOptions (): any[] {
            const showAccounts = activeSubHasPurchasedProductLine(PRODUCT_LINES.CORE, 'accountsUIEnabled');
            if (!showAccounts) {
                return [];
            }
            const typesToDisplay = ['boolean', 'string', 'integer'];
            const meta = this.metadataListByKind('account', { groupsToExclude: ['pendo'] }).filter((metadata: any) => {
                return typesToDisplay.includes(metadata.Type);
            });

            return meta.map((el: any) => {
                return {
                    id: `${el.kind}.${el.group}.${el.field}`,
                    description: el.description,
                    DisplayName: el.DisplayName,
                    tooltip: el.DisplayName,
                    group: el.group,
                    Type: el.Type,
                    field: el.field,
                    kind: el.kind,
                };
            });
        },
        visitorMetadataOptionsWithAutoGroup (): any[] {
            const showAccounts = activeSubHasPurchasedProductLine(PRODUCT_LINES.CORE, 'accountsUIEnabled');
            const typesToDisplay = ['boolean', 'string', 'integer'];
            const meta = this.metadataListByKind('visitor', { groupsToExclude: ['pendo'] }).filter((metadata: any) => {
                const hideAccountFields = !showAccounts && (metadata.field.includes('account'));

                return typesToDisplay.includes(metadata.Type) && !hideAccountFields;
            });

            return meta.map((el: any) => {
                return {
                    id: `${el.kind}.${el.group}.${el.field}`,
                    DisplayName: el.DisplayName,
                    description: el.description,
                    tooltip: el.DisplayName,
                    group: el.group,
                    Type: el.Type,
                    field: el.field,
                    kind: el.kind,
                };
            });
        },
        parentAccountMetadataOptions (): any[] {
            const showAccounts = activeSubHasPurchasedProductLine(PRODUCT_LINES.CORE, 'accountsUIEnabled');
            if (!showAccounts) {
                return [];
            }
            const typesToDisplay = ['boolean', 'string', 'integer'];
            const meta = this
                .metadataListByKind('parentAccount', { groupsToExclude: ['pendo'] })
                .filter((metadata: any) => {
                    return typesToDisplay.includes(metadata.Type);
                });

            return meta.map((el: any) => {
                return {
                    id: `${el.kind}.${el.group}.${el.field}`,
                    DisplayName: el.DisplayName,
                    description: el.description,
                    tooltip: el.DisplayName,
                    group: el.group,
                    Type: el.Type,
                    field: el.field,
                    kind: el.kind,
                };
            });
        },
        emailMetadataOptions: (state) => getEmailMetadataOptions(state.map, {
            visitor: $t('metadata.Visitor'),
            account: $t('metadata.Account'),
        }),
        objectMetadataMap: (state): Record<string, Record<string, any>> => state.objectMap || {},
        // Object-property FieldSchemas live on either the 'event' or 'historical' side
        objectPropertyEntriesByField (state): Record<string, any> {
            const map: Record<string, any> = {};
            ['event', 'historical'].forEach((kind) => {
                iterateThroughMetadata(get(state.map, kind, []), kind).forEach((entry: any) => {
                    if (!entry?.objectProperty) return;
                    if (map[entry.field]?.nameMetadata && !entry.nameMetadata) return;

                    map[entry.field] = entry;
                });
            });

            return map;
        },
        objectNameMetadataByProperty (): Record<string, string> {
            const map: Record<string, string> = {};
            Object.values(this.objectPropertyEntriesByField).forEach((entry) => {
                if (!entry.nameMetadata) return;

                map[entry.field] = entry.nameMetadata;
            });

            return map;
        },
        // All configured object-metadata fields for display in the UI
        allObjectMetadataOptions (state): any[] {
            const objectMap = state.objectMap || {};
            const objectPropertyEntries = this.objectPropertyEntriesByField;
            const options: any[] = [];

            Object.keys(objectMap).forEach((objectProperty) => {
                const fields = objectMap[objectProperty] || {};

                Object.keys(fields).forEach((field) => {
                    const schema = fields[field] || {};
                    let displayName = field;

                    if (schema.DisplayName) {
                        displayName = schema.DisplayName;
                    }

                    options.push({
                        id: objectMetadataRowPath(objectProperty, field),
                        kind: 'objectMetadata',
                        objectProperty,
                        group: objectProperty,
                        groupDisplayName: objectPropertyEntries[objectProperty]?.DisplayName || objectProperty,
                        field,
                        DisplayName: displayName,
                        displayName,
                        description: schema.description || '',
                        Type: schema.Type,
                        ElementType: schema.ElementType,
                        ElementFormat: schema.ElementFormat,
                    });
                });
            });

            return sortBy(options, [
                ({ groupDisplayName }: any) => groupDisplayName.toLowerCase(),
                ({ displayName }: any) => displayName.toLowerCase(),
            ]);
        },
        // Scalar-only subset for filters/aggregations; use allObjectMetadataOptions for display.
        objectMetadataOptions (): any[] {
            const typesToDisplay = ['boolean', 'string', 'integer'];

            return this.allObjectMetadataOptions.filter((option) => typesToDisplay.includes(option.Type));
        },
        objectMetadataOptionsByObjectProperty (): (allowedObjectProperties?: ReadonlyArray<string>) => any[] {
            return (allowedObjectProperties?: ReadonlyArray<string>) => {
                const options = this.objectMetadataOptions;
                if (!allowedObjectProperties) return options;
                const allowed = new Set(allowedObjectProperties);

                return options.filter((option) => allowed.has(option.objectProperty));
            };
        },
    },
    actions: {
        setMap ({ map }: { map: Record<string, any> }) {
            this.map = map;
        },
        setObjectMap ({ objectMap }: { objectMap: Record<string, Record<string, any>> }) {
            this.objectMap = objectMap;
        },
        setMapAtKey ({ kind, metadata }: { kind: string; metadata: any }) {
            this.map[kind] = metadata;
        },
        deleteMapKey ({ kind }: { kind: string }) {
            delete this.map[kind];
        },
        setError ({ error }: { error: any }) {
            this.error = error;
        },
        setFetching ({ fetching }: { fetching: boolean }) {
            this.fetching = fetching;
        },
        setUpdating ({ updating }: { updating: boolean }) {
            this.updating = updating;
        },
        setRemoving ({ removing }: { removing: boolean }) {
            this.removing = removing;
        },
        setIncludesCardinality ({ cardinality }: { cardinality: boolean }) {
            this.includesCardinality = cardinality;
        },
        setUpdate ({ kind, group, field, schema }: { kind: string; group: string; field: string; schema: any }) {
            if (!get(this.map[kind], group)) {
                this.map[kind][group] = {};
            }

            this.map[kind][group][field] = schema;
        },
        setRules ({ kind, group, field, rules }: { kind: string; group: string; field: string; rules: any }) {
            this.map[kind][group][field].eventUrlRules = rules;
        },
        setObjectPropertySkipList ({ kind, group, field, objectPropertySkipList }: { kind: string; group: string; field: string; objectPropertySkipList: any }) {
            this.map[kind][group][field].objectPropertySkipList = objectPropertySkipList;
        },
        setAutoSkipList ({ kind, group, field, autoSkipList }: { kind: string; group: string; field: string; autoSkipList: any }) {
            this.map[kind][group][field].autoSkipList = autoSkipList;
        },
        setObjectPropertyAction ({ kind, group, field, at }: { kind: string; group: string; field: string; at: any }) {
            this.map[kind][group][field].objectProperty = at;
        },
        setDelete ({ kind, group, field }: { kind: string; group: string; field: string }) {
            delete this.map[kind][group][field];
        },

        async loadAll ({ cardinality = false, reFetch = false }: { cardinality?: boolean; reFetch?: boolean } = {}) {
            const hasRequiredCardinality = !cardinality || this.includesCardinality;
            if (!isEmpty(this.map) && !reFetch && hasRequiredCardinality) return;

            // Join a batch that is already in flight when it will satisfy this caller
            if (loadAllPromise && !reFetch && (!cardinality || loadAllPromise.cardinality)) {
                await loadAllPromise.promise;

                return;
            }

            // Never fetch less than what is already loaded or in flight, so a later
            // batch can't downgrade a map that includes cardinality
            const effectiveCardinality = cardinality || this.includesCardinality || !!loadAllPromise?.cardinality;

            this.setFetching({ fetching: true });

            // Claim the slot before any work runs so a synchronous throw still releases it
            const batch = { cardinality: effectiveCardinality } as LoadAllBatch;
            const seq = ++loadAllSeq;
            loadAllPromise = batch;
            batch.promise = (async () => {
                try {
                    const includeParentAccounts = hasSubscriptionFlag('usesSubaccounts');

                    const fetchPromises: Record<string, any> = {
                        visitor: fetchVisitorMetadata({ cardinality: effectiveCardinality }),
                        account: fetchAccountMetadata({ cardinality: effectiveCardinality }),
                    };

                    if (includeParentAccounts) {
                        fetchPromises.parentAccount = fetchParentAccountMetadata({ cardinality: effectiveCardinality });
                    }

                    // No cardinality includes all event metadata
                    fetchPromises.eventNoCardinality = fetchEventMetadata({ cardinality: false });

                    // Cardinality excludes event metadata with > 300 unique values, merge them together below
                    if (effectiveCardinality) {
                        fetchPromises.eventWithCardinality = fetchEventMetadata({ cardinality: true });
                    }

                    fetchPromises.objectMetadata = fetchObjectMetadata().catch(() => null);

                    const result: any = await promiseAllObjectValues(fetchPromises);
                    const { visitor, account, parentAccount, eventNoCardinality, eventWithCardinality, objectMetadata } = result;
                    const map: Record<string, any> = { visitor: visitor.data, account: account.data };
                    if (parentAccount && includeParentAccounts) {
                        map.parentAccount = parentAccount.data;
                    }

                    // Cardinality excludes event metadata with > 300 unique values, merge them together
                    if (eventNoCardinality) {
                        map.event = merge(eventNoCardinality.data, eventWithCardinality ? eventWithCardinality.data : {});
                    }

                    // Never let an older batch, or a result without cardinality, replace what a
                    // newer batch or a map that already has cardinality put in place
                    if (seq > appliedLoadAllSeq && (!this.includesCardinality || effectiveCardinality)) {
                        appliedLoadAllSeq = seq;
                        this.setIncludesCardinality({ cardinality: effectiveCardinality });
                        this.setMap({ map });
                        this.setObjectMap({ objectMap: (objectMetadata?.data as Record<string, Record<string, any>>) || {} });
                    }
                } catch (error) {
                    this.setError({ error });
                    throw error;
                } finally {
                    // The newest batch owns the in-flight slot and the fetching flag; an older
                    // batch that is still running does not take them back
                    if (loadAllPromise === batch) {
                        loadAllPromise = null;
                        this.setFetching({ fetching: false });
                    }
                }
            })();

            await batch.promise;
        },

        async updateObjectPropertyNameMetadata ({ kind, group, field, fieldName }: {
            kind: string; group: string; field: string; fieldName: string;
        }) {
            this.setUpdating({ updating: true });
            try {
                await setObjectPropertyNameMetadata({ kind, group, field }, { nameMetadata: fieldName });
                const schema = get(this.map, [kind, group, field]);
                if (schema && typeof schema === 'object') {
                    schema.nameMetadata = fieldName;
                }
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setUpdating({ updating: false });
            }
        },

        async updateObjectMetadataSchema ({ objectProperty, field, schema }: {
            objectProperty: string; field: string; schema: Record<string, any>;
        }) {
            this.setUpdating({ updating: true });
            try {
                const { data }: any = await updateSchema(
                    { kind: 'eventPropertyMetadata', group: objectProperty, field },
                    schema,
                );
                if (!this.objectMap[objectProperty]) this.objectMap[objectProperty] = {};
                this.objectMap[objectProperty][field] = data;
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setUpdating({ updating: false });
            }
        },

        async deleteObjectMetadataSchema ({ objectProperty, field }: {
            objectProperty: string; field: string;
        }) {
            this.setRemoving({ removing: true });
            try {
                await removeSchema({ kind: 'eventPropertyMetadata', group: objectProperty, field });
                if (this.objectMap[objectProperty]) {
                    delete this.objectMap[objectProperty][field];
                    if (Object.keys(this.objectMap[objectProperty]).length === 0) {
                        delete this.objectMap[objectProperty];
                    }
                }
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setRemoving({ removing: false });
            }
        },

        update ({ schema = {} }: { schema?: any }) {
            const { kind, group, field, ...data } = schema;

            this.setUpdating({ updating: true });

            return updateSchema({ kind, group, field }, data)
                .then(({ data }: any) => {
                    this.setUpdating({ updating: false });
                    this.setUpdate({ kind, group, field, schema: data });
                })
                .catch((error: any) => {
                    this.setUpdating({ updating: false });
                    this.setError({ error });
                    throw error;
                });
        },

        async updateRules ({ kind, group, field, rules, objectPropertySkipList }: { kind: string; group: string; field: string; rules?: any; objectPropertySkipList?: any }) {
            this.setUpdating({ updating: true });

            const applyResponse = (data: any) => {
                if (!data || typeof data !== 'object') return;
                if ('eventUrlRules' in data) {
                    this.setRules({ kind, group, field, rules: data.eventUrlRules ?? [] });
                }
                if ('objectPropertySkipList' in data) {
                    this.setObjectPropertySkipList({
                        kind,
                        group,
                        field,
                        objectPropertySkipList: data.objectPropertySkipList ?? [],
                    });
                }
                if ('autoSkipList' in data) {
                    this.setAutoSkipList({ kind, group, field, autoSkipList: data.autoSkipList ?? [] });
                }
            };

            try {
                if (rules !== undefined) {
                    const { data } = await updateRules({ kind, group, field }, { rules });
                    applyResponse(data);
                    if (!data || !('eventUrlRules' in data)) {
                        this.setRules({ kind, group, field, rules });
                    }
                }
                if (objectPropertySkipList !== undefined) {
                    const { data } = await setSkipList({ kind, group, field }, { objectPropertySkipList });
                    applyResponse(data);
                    if (!data || !('objectPropertySkipList' in data)) {
                        this.setObjectPropertySkipList({ kind, group, field, objectPropertySkipList });
                    }
                }
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setUpdating({ updating: false });
            }
        },

        setObjectProperty ({ kind, group, field, at }: { kind: string; group: string; field: string; at: any }) {
            this.setUpdating({ updating: true });

            return setObjectProperty({ kind, group, field }, { at })
                .then(({ data }: any) => {
                    this.setUpdating({ updating: false });
                    const resolvedAt = data && 'at' in data ? data.at : at;
                    this.setObjectPropertyAction({ kind, group, field, at: resolvedAt });
                })
                .catch((error: any) => {
                    this.setUpdating({ updating: false });
                    this.setError({ error });
                    throw error;
                });
        },

        updateViaEnabled ({ schema = {} }: { schema?: any }) {
            const { kind, group, field } = schema;

            this.setUpdating({ updating: true });

            return updateViaEnabled({ kind, group, field })
                .then(({ data }: any) => {
                    this.setUpdating({ updating: false });
                    this.setUpdate({ kind, group, field, schema: data });
                })
                .catch((error: any) => {
                    this.setUpdating({ updating: false });
                    this.setError({ error });
                    throw error;
                });
        },

        remove ({ schema = {} }: { schema?: any }) {
            const { kind, group, field } = schema;

            this.setRemoving({ removing: true });

            return removeSchema({ kind, group, field })
                .then(() => {
                    this.setRemoving({ removing: false });
                    this.setDelete({ kind, group, field });
                })
                .catch((error: any) => {
                    this.setRemoving({ removing: false });
                    this.setError({ error });
                    throw error;
                });
        },

        async promote ({ schema = {}, promote }: { schema?: any; promote: any }) {
            const { kind, group, field } = schema;
            this.setUpdating({ updating: true });

            let response: any;
            try {
                response = await promoteSchema({ kind, group, field }, { promote });
            } catch (error) {
                this.setUpdating({ updating: false });
                this.setError({ error });
                throw error;
            }
            this.setUpdating({ updating: false });
            this.setUpdate({ kind, group, field, schema: response.data });
        },

        async generateDescription ({ kind, group, field, displayName, type, sample, description }: {
            kind: string; group: string; field: string; displayName: string; type: string; sample: any; description: string;
        }) {
            this.setUpdating({ updating: true });

            try {
                const response = await generateMetadataDescription({
                    kind,
                    group,
                    field,
                    displayName,
                    type,
                    sample,
                    description,
                });
                this.setUpdating({ updating: false });

                return response.data;
            } catch (error) {
                this.setUpdating({ updating: false });
                this.setError({ error });
                throw error;
            }
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
    loadAllSeq = 0;
    appliedLoadAllSeq = 0;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useMetadataStore, module.hot));
}
